import { useCallback } from "react";
import { CalculateButton } from "../../components/common/CalculateButton";
import { DownloadButton } from "../../components/common/DownloadButton";
import { useAppDispatch, useAppSelector } from "../../store/hooks/hooks";
import { InputRow } from "./InputRow";
import { useCalculatorMutation } from "../../store/slices/apiSlice";
import { useTranslation } from "react-i18next";
import { getDataForGRC as vlpDataSelector } from "../../store/slices/vlpCalculatorSlice";
import { getDataForGRC as pvtDataSelector } from "../../store/slices/pvtCalculatorSlice";
import { validateInputs } from "../../store/slices/gasRateCriticalCalculatorSlice";
import { OuterData } from "../../components/common/OuterData";
import { getSecondWellIsOpen } from "../../store/slices/appSlice";

const InputSection = () => {
	const inputs = useAppSelector(state => state.gasRateCriticalCalculator.inputs);
	const [triggerCalculator] = useCalculatorMutation();
	const { t } = useTranslation("grc");
	const dispatch = useAppDispatch();
	const secondWell = useAppSelector(getSecondWellIsOpen);

	const validated = (inputs: { error?: string | null }[]) => {
		if (inputs.some(input => input.error != null))
			return false;

		return true;
	};

	const pvtData = useAppSelector(pvtDataSelector);
	const vlpData1 = useAppSelector(state => vlpDataSelector(state, "well1"));
	const vlpData2 = useAppSelector(state => vlpDataSelector(state, "well2"));

	const onCalculate = useCallback(async () => {
		dispatch(validateInputs());

		if (!validated([...inputs, ...vlpData1.inputs, ...pvtData.inputs]) || (secondWell && !validated(vlpData2.inputs)) || pvtData.errors.length > 0)
			return;

		const formData1 = getFormData("well1");
		if (formData1)
			triggerCalculator({
				calculator_type: "gas_rate_critical",
				well: "1",
				...formData1,
			});

		if (secondWell) {
			const formData2 = getFormData("well2");
			if (formData2)
				triggerCalculator({
					calculator_type: "gas_rate_critical",
					well: "2",
					...formData2,
				});
		}
	}, [dispatch, triggerCalculator, inputs, pvtData, vlpData1, vlpData2, secondWell]);

	const getFormData = useCallback((wellId: "well1" | "well2") => {
		dispatch(validateInputs());

		if (!validated(inputs) || (wellId === "well1" && !validated(vlpData1.inputs)) || (wellId === "well2" && !validated(vlpData2.inputs)) || !validated(pvtData.inputs))
			return;

		const formData = inputs.reduce((acc, input) => {
			if (input.queryName != "pressure_wf" || (input.baseValue != null && input.baseValue !== 0))
				acc[input.queryName] = input.baseValue ?? 0;

			return acc;
		}, {} as Record<string, string | number>);

		const vlpInputs = wellId === "well1" ? vlpData1.inputs : vlpData2.inputs;

		[...vlpInputs, ...pvtData.inputs].forEach(input => {
			formData[input.queryName] = input.baseValue ?? 0;
		});

		return formData;
	}, [dispatch, inputs, vlpData1, vlpData2, pvtData, secondWell]);

	return (
		<div className="grc__inputs-section">
			<fieldset className="grc__inputs round-corner-container">
				<legend>{t(`main_parameters`)}</legend>
				{inputs.map((input) => (
					<InputRow
						key={`grc__${input.id}`}
						inputId={input.id}
					/>
				))}
			</fieldset>
			<div className="vlp__buttons">
				<CalculateButton onClick={onCalculate} disabled={false} />
				<DownloadButton getFormData={() => getFormData("well1")} calculatorType="gas_rate_critical" disabled={false} title={t(`download`)} />
				{secondWell && (
					<DownloadButton getFormData={() => getFormData("well2")} calculatorType="gas_rate_critical" disabled={false} title={t(`download`)} />
				)}
			</div>
			<OuterData
				inputs={pvtData.inputs}
				errors={pvtData.errors}
				translationNamespace="pvt"
			/>
			<OuterData
				inputs={vlpData1.inputs}
				errors={vlpData1.errors}
				translationNamespace="vlp"
				wellId="well1"
			/>
			{secondWell && (
				<OuterData
					inputs={vlpData2.inputs}
					errors={vlpData2.errors}
					translationNamespace="vlp"
					wellId="well2"
				/>
			)}
		</div >
	)
}

export default InputSection;